import { h, Component, Fragment } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

// Bottom drawer for the guest (not logged in) webshop basket. All state lives in
// guestCartStore; this component only renders it and forwards clicks. Opened
// with appState.openDrawer('guestCart', { race }, level) from the race page and
// the product drawer.
const FORM_FIELDS = [
    {
        name: 'firstname', autocomplete: 'given-name', type: 'text', label: <Text id='guestCart.firstname'>First name</Text>, error: <Text id='guestCart.error-firstname'>Enter your first name</Text>,
    },
    {
        name: 'lastname', autocomplete: 'family-name', type: 'text', label: <Text id='guestCart.lastname'>Last name</Text>, error: <Text id='guestCart.error-lastname'>Enter your last name</Text>,
    },
    {
        name: 'cellphone', autocomplete: 'tel', type: 'tel', label: <Text id='guestCart.cellphone'>Mobile number</Text>, error: <Text id='guestCart.error-cellphone'>Enter a valid mobile number</Text>,
    },
    {
        name: 'email', autocomplete: 'email', type: 'email', label: <Text id='guestCart.email'>E-mail</Text>, error: <Text id='guestCart.error-email'>Enter a valid e-mail address</Text>,
    },
];

const lineKeyOf = line => `${line.id}|${line.color || ''}|${line.size || ''}`;

@observer
class GuestCartDrawer extends Component {
    onFormInput = (e) => {
        const { guestCartStore } = this.props.stores;
        const { name, value } = e.target;
        guestCartStore.setFormField(name, value);
    }

    onQtyClick = (e) => {
        const { guestCartStore } = this.props.stores;
        const { key, delta } = e.currentTarget.dataset;
        const line = guestCartStore.lines.find(l => lineKeyOf(l) === key);
        if (!line) {
            return;
        }
        guestCartStore.setQty(key, line.qty + parseInt(delta, 10));
    }

    onRemoveClick = (e) => {
        const { guestCartStore } = this.props.stores;
        guestCartStore.remove(e.currentTarget.dataset.key);
    }

    onPayClick = async (e) => {
        e.preventDefault();
        const { guestCartStore } = this.props.stores;
        const { race } = this.props;
        const wantedPage = typeof window !== 'undefined' ? window.location.href : '';
        await guestCartStore.checkout({ race, wantedPage });
    }

    // The page normally unloads for Vipps; this is the way back when it did not
    // (blocked redirect, or the browser restored the page from its back cache).
    onBackToBasket = () => {
        const { guestCartStore } = this.props.stores;
        guestCartStore.updateKeyValue('redirecting', false);
    }

    onClose = () => {
        const { appState, guestCartStore } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        if (guestCartStore.paid) {
            guestCartStore.clear();
        }
        appState.toggleDrawer(false, drawerLevel);
    }

    renderPaid() {
        const { guestCartStore } = this.props.stores;
        const { sale, form } = guestCartStore;
        const number = (sale && (sale.saleNumber || sale.id)) || '';
        return (
            <div class='d-flex flex-column align-items-center text-center px-3 py-4'>
                <div class='display-3 text-success mb-3'><i class='fa-duotone fa-circle-check' /></div>
                <h4><Text id='guestCart.thanks'>Thank you for your order!</Text></h4>
                <p class='mb-1'><Text id='guestCart.order-number' fields={{ number }}>Order number {number}</Text></p>
                <p class='text-body-secondary'><Text id='guestCart.receipt-sent' fields={{ email: form.email }}>A receipt has been sent to {form.email}</Text></p>
                <button type='button' class='btn btn-primary rounded-pill mt-3' onClick={this.onClose}>
                    <Text id='guestCart.close'>Close</Text>
                </button>
            </div>
        );
    }

    renderRedirecting() {
        return (
            <div class='d-flex flex-column align-items-center text-center px-3 py-5'>
                <i class='fa-solid fa-spinner fa-spin fa-2x mb-3' />
                <p><Text id='guestCart.redirecting'>Taking you to Vipps…</Text></p>
                <button type='button' class='btn btn-link' onClick={this.onBackToBasket}>
                    <Text id='guestCart.back-to-basket'>Back to the basket</Text>
                </button>
            </div>
        );
    }

    renderLine(line) {
        const key = lineKeyOf(line);
        const variant = [line.color, line.size].filter(v => !!v).join(' / ');
        const lineTotal = Math.round((line.price || 0) * (1 + (line.vat || 0) / 100) * line.qty);
        return (
            <li class='list-group-item d-flex flex-row align-items-center' key={key}>
                {line.image ? <img src={line.image} class='rounded me-2' style='width: 48px; height: 48px; object-fit: cover;' alt='' /> : <span class='me-2 text-body-secondary' style='width: 48px;'><i class='fa-duotone fa-box fa-lg' /></span>}
                <div class='flex-fill'>
                    <div>{line.name}</div>
                    {variant && <small class='text-body-secondary'>{variant}</small>}
                </div>
                <div class='d-flex flex-row align-items-center me-2'>
                    <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' data-key={key} data-delta='-1' onClick={this.onQtyClick}><i class='fa-solid fa-minus' /></button>
                    <span class='mx-2'>{line.qty}</span>
                    <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' data-key={key} data-delta='1' onClick={this.onQtyClick}><i class='fa-solid fa-plus' /></button>
                </div>
                <div class='text-end' style='min-width: 70px;'>{util.format(lineTotal, 0, ',', ' ')} kr</div>
                <button type='button' class='btn btn-sm btn-link text-danger ms-2' data-key={key} onClick={this.onRemoveClick} title='Fjern'>
                    <i class='fa-duotone fa-trash' /><span class='visually-hidden'><Text id='guestCart.remove'>Remove</Text></span>
                </button>
            </li>
        );
    }

    renderBasket() {
        const { guestCartStore } = this.props.stores;
        const {
            lines, total, form, formErrors = {}, checkoutError, checkingOut, paymentCancelled,
        } = guestCartStore;
        return (
            <form class='d-flex flex-column px-3 pb-5' onSubmit={this.onPayClick} noValidate>
                {paymentCancelled && <div class='alert alert-warning mb-3'>
                    <Text id='guestCart.payment-not-completed'>The payment was not completed. Your items are still in the basket.</Text>
                </div>}
                <ul class='list-group mb-3'>
                    {lines.map(line => this.renderLine(line))}
                </ul>
                <div class='d-flex flex-row justify-content-between fs-5 mb-3 px-2'>
                    <span><Text id='guestCart.total'>Total</Text></span>
                    <strong>{util.format(total, 0, ',', ' ')} kr</strong>
                </div>

                <h6 class='mt-2'><Text id='guestCart.your-details'>Your details</Text></h6>
                <div class='row g-2'>
                    {FORM_FIELDS.map(field => (
                        <div class={field.name === 'firstname' || field.name === 'lastname' ? 'col-6' : 'col-12'} key={field.name}>
                            <label class='form-label small mb-1' for={`guestCart-${field.name}`}>{field.label}</label>
                            <input
                                id={`guestCart-${field.name}`}
                                name={field.name}
                                type={field.type}
                                autocomplete={field.autocomplete}
                                class={`form-control ${formErrors[field.name] ? 'is-invalid' : ''}`}
                                value={form[field.name] || ''}
                                onInput={this.onFormInput}
                            />
                            {formErrors[field.name] && <div class='invalid-feedback'>{field.error}</div>}
                        </div>
                    ))}
                </div>

                {checkoutError && <div class='alert alert-danger mt-3 mb-0'>{checkoutError}</div>}

                <button type='submit' class='btn btn-lg btn-primary rounded-pill mt-3' disabled={checkingOut}>
                    {checkingOut ? <i class='fa-solid fa-spinner fa-spin me-2' /> : <i class='fa-duotone fa-mobile me-2' />}
                    <Text id='guestCart.pay-with-vipps'>Pay with Vipps</Text>
                </button>
                <small class='text-body-secondary text-center mt-2'>
                    <Text id='guestCart.no-account-needed'>No account needed. We only use your details for this order.</Text>
                </small>
            </form>
        );
    }

    render() {
        const { guestCartStore } = this.props.stores;
        const { lines, paid, redirecting } = guestCartStore;

        return (
            <div class='w-100 h-100 bg-body text-body d-flex flex-column'>
                <div class='font-weight-lighter px-3 py-2 box-header d-flex flex-row justify-content-between align-items-center'>
                    <span><i class='fa-duotone fa-basket-shopping me-2' /><Text id='guestCart.title'>Basket</Text></span>
                </div>
                {paid ? this.renderPaid() : <>
                    {redirecting ? this.renderRedirecting() : <>
                        {lines.length === 0 ? (
                            <div class='text-center text-body-secondary px-3 py-5'>
                                <i class='fa-duotone fa-basket-shopping fa-3x mb-3' />
                                <p><Text id='guestCart.empty'>Your basket is empty.</Text></p>
                            </div>
                        ) : this.renderBasket()}
                    </>}
                </>}
            </div>
        );
    }
}

export default GuestCartDrawer;
